/**
 * <PushNotificationsToggle />
 *
 * Single-row UI surface for managing this device's Web Push subscription.
 * Designed to slot into the dashboard sidebar's user menu, the profile
 * page, or any settings card — small, dark-mode-aware, fully self-contained.
 *
 * State machine:
 *   • UNSUPPORTED  → browser missing PushManager / serviceWorker
 *   • DENIED       → user permanently said no in the browser settings
 *   • IDLE         → permission default; click "Enable" to subscribe
 *   • SUBSCRIBED   → button shows "Disable" + a "Test" button
 *
 * One device → one subscription. Persists the latest `endpoint` so
 * subsequent visits can show the right state immediately.
 */
import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

// Helper: base64url string → Uint8Array (required by PushManager)
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

const SUPPORTED = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

export default function PushNotificationsToggle({ compact = false }) {
  const [state, setState] = useState('loading'); // loading | unsupported | denied | idle | subscribed
  const [busy, setBusy] = useState(false);

  // Determine current state on mount.
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!SUPPORTED) { if (!cancel) setState('unsupported'); return; }
      if (Notification.permission === 'denied') { if (!cancel) setState('denied'); return; }
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancel) setState(sub ? 'subscribed' : 'idle');
      } catch {
        if (!cancel) setState('idle');
      }
    })();
    return () => { cancel = true; };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error(permission === 'denied'
          ? 'Notifications are blocked. Enable them in your browser settings.'
          : 'Notification permission required.');
        setState(permission === 'denied' ? 'denied' : 'idle');
        return;
      }
      const reg = await navigator.serviceWorker.register('/sw.js');
      const { data } = await api.get('/push/public-key');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      const json = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        device_label: navigator.platform || null,
      });
      setState('subscribed');
      toast.success('Push notifications enabled on this device.');
    } catch (e) {
      console.error('push enable failed', e);
      toast.error(e?.response?.data?.detail || 'Could not enable notifications.');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        try {
          await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
        } catch { /* even if backend fails, kill it locally */ }
        await sub.unsubscribe();
      }
      setState('idle');
      toast.success('Push notifications disabled on this device.');
    } catch (e) {
      toast.error('Could not disable notifications.');
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/push/test', {
        title: 'Vigilant · test alert',
        body: 'If you can see this, push notifications are wired up.',
        url: '/dashboard',
      });
      if (data?.summary?.sent > 0) {
        toast.success('Test sent — check your notifications.');
      } else {
        toast.warning('No devices reachable. Try toggling off and on again.');
      }
    } catch (e) {
      toast.error('Test send failed.');
    } finally { setBusy(false); }
  };

  // ── Compact rendering for sidebar menus etc. ──
  if (compact) {
    if (state === 'unsupported' || state === 'denied') return null;
    if (state === 'loading') {
      return (
        <button disabled className="text-xs opacity-60 flex items-center gap-1.5 px-2 py-1">
          <RefreshCw size={12} className="animate-spin"/> Checking notifications…
        </button>
      );
    }
    return (
      <button
        onClick={state === 'subscribed' ? disable : enable}
        disabled={busy}
        data-testid="push-toggle-compact"
        className="text-xs flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/5 disabled:opacity-50"
      >
        {state === 'subscribed' ? <BellRing size={12} className="text-emerald-400"/> : <Bell size={12}/>}
        {state === 'subscribed' ? 'Notifications on' : 'Enable notifications'}
      </button>
    );
  }

  // ── Full card rendering ──
  return (
    <div className="rounded-lg border border-white/10 dark:border-white/10 bg-white/5 p-4" data-testid="push-toggle">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
          state === 'subscribed' ? 'bg-emerald-500/15' : 'bg-white/5'
        }`}>
          {state === 'subscribed'
            ? <BellRing size={16} className="text-emerald-400"/>
            : state === 'denied' || state === 'unsupported'
              ? <BellOff size={16} className="opacity-60"/>
              : <Bell size={16}/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Push notifications</div>
          <p className="text-xs opacity-65 mt-0.5 leading-relaxed">
            {state === 'unsupported' && 'This browser does not support web push notifications.'}
            {state === 'denied' && 'Notifications are blocked. Open browser settings and re-allow them for this site.'}
            {state === 'loading' && 'Checking subscription…'}
            {state === 'idle' && 'Get real-time alerts on this device for brute-force lockouts, audit events, and sovereignty drops.'}
            {state === 'subscribed' && 'You will receive real-time alerts on this device.'}
          </p>
          <div className="flex items-center gap-2 mt-3">
            {state === 'idle' && (
              <button
                onClick={enable}
                disabled={busy}
                data-testid="push-enable-btn"
                className="px-3 py-1.5 text-xs font-semibold bg-[var(--brand-primary,#8B3FC5)] text-white rounded-md hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Bell size={12}/> Enable on this device
              </button>
            )}
            {state === 'subscribed' && (
              <>
                <button
                  onClick={sendTest}
                  disabled={busy}
                  data-testid="push-test-btn"
                  className="px-3 py-1.5 text-xs font-semibold border border-white/15 rounded-md hover:bg-white/5 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <BellRing size={12}/> Send test
                </button>
                <button
                  onClick={disable}
                  disabled={busy}
                  data-testid="push-disable-btn"
                  className="px-3 py-1.5 text-xs text-rose-300 border border-rose-400/30 hover:bg-rose-500/10 rounded-md disabled:opacity-50 flex items-center gap-1.5"
                >
                  <BellOff size={12}/> Disable
                </button>
              </>
            )}
            {busy && <RefreshCw size={12} className="animate-spin opacity-60"/>}
          </div>
        </div>
      </div>
    </div>
  );
}
